import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { RetryService } from './retry.service';

@Injectable({
  providedIn: 'root'
})
export class GeminiService {
  // Backend API URL from environment
  private apiBaseUrl = environment.apiBaseUrl || 'https://hcus-wa-api-app-dev-etcdg4eng6egbtct.eastus-01.azurewebsites.net';
  private geminiStreamUrl = `${this.apiBaseUrl}/api/v1/gemini/stream`;
  private geminiCountTokensUrl = `${this.apiBaseUrl}/api/v1/gemini/count-tokens`;

  // Use environment-based db_name
  private dbName = environment.dbName;

  // Track active AbortController so we can cancel in-flight requests
  private activeAbortController: AbortController | null = null;

  constructor(
    private http: HttpClient,
    private retryService: RetryService
  ) {}

  /**
   * Abort any currently active Gemini stream request.
   * Call this when the user starts a new chat, navigates away, or wants to stop generation.
   */
  abortActiveStream(): void {
    if (this.activeAbortController) {
      console.log('[GeminiService] Aborting active stream request');
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
  }

  /**
   * Stream content from Gemini API via backend with automatic retry (up to 3 attempts)
   * Returns an Observable that emits chunks of text as they arrive
   * @param model Model name (e.g., 'gemini-2.5-flash-001')
   * @param contents Conversation contents
   * @param generationConfig Generation configuration
   * @param systemInstruction System instruction
   * @param tools Tools configuration
   * @param userId Optional user ID for logging
   * @param sessionId Optional session ID for logging
   */
  streamContent(
    model: string,
    contents: any[],
    generationConfig?: any,
    systemInstruction?: string,
    tools?: any,
    userId?: string,
    sessionId?: string,
    disableThinking?: boolean
  ): Observable<string> {
    const subject = new Subject<string>();

    const payload: any = {
      model,
      contents,
      generationConfig,
      systemInstruction,
      tools,
      db_name: this.dbName,  // v2 for dev
      user_id: userId,
      session_id: sessionId
    };
    if (disableThinking) {
      payload.thinkingConfig = { thinkingBudget: 0 };
    }

    // Cancel any previous in-flight stream
    this.abortActiveStream();

    // Create a new AbortController for this request
    const abortController = new AbortController();
    this.activeAbortController = abortController;

    // Overall timeout: auto-abort after 120 seconds
    const overallTimeout = setTimeout(() => {
      console.warn('[GeminiService] Overall stream timeout reached (120s), aborting');
      abortController.abort();
    }, 120_000);

    // Function to make a single streaming attempt
    const attemptStream = (attemptNumber: number, maxAttempts: number = 3): Promise<void> => {
      console.log(`[GeminiService] Stream attempt ${attemptNumber}/${maxAttempts}`);
      
      return fetch(this.geminiStreamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortController.signal
      }).then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            const errorMsg = `HTTP error! status: ${response.status}, message: ${text}`;
            console.error(`[GeminiService] Attempt ${attemptNumber} failed:`, errorMsg);
            
            // Retry on server errors (5xx)
            if (response.status >= 500 && attemptNumber < maxAttempts) {
              const delay = Math.min(Math.pow(2, attemptNumber - 1) * 1000, 10000);
              console.log(`[GeminiService] Retrying in ${delay}ms...`);
              return new Promise<void>((resolve, reject) => {
                setTimeout(() => {
                  attemptStream(attemptNumber + 1, maxAttempts).then(resolve).catch(reject);
                }, delay);
              });
            }
            
            throw new Error(errorMsg);
          });
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('Response body is null');
        }

        let buffer = '';
        let lastChunkText = '';
        let repetitiveCount = 0;
        const MAX_REPETITIVE = 10; // Auto-abort after 10 identical chunks

        const readStream = (): Promise<void> => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              clearTimeout(overallTimeout);
              this.activeAbortController = null;
              subject.complete();
              return;
            }

            // Check if aborted
            if (abortController.signal.aborted) {
              clearTimeout(overallTimeout);
              this.activeAbortController = null;
              reader.cancel();
              subject.error({
                status: 499,
                message: 'Request was cancelled.',
                userFriendlyMessage: 'Request was cancelled. You can continue your conversation.',
                can_continue: true
              });
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            
            // Keep the last incomplete line in buffer
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;
              
              if (line.startsWith('data: ')) {
                try {
                  const dataStr = line.substring(6).trim();
                  if (dataStr === '[DONE]') {
                    clearTimeout(overallTimeout);
                    this.activeAbortController = null;
                    subject.complete();
                    return;
                  }
                  
                  const data = JSON.parse(dataStr);
                  
                  // Handle error responses from backend
                  if (data.error) {
                    console.error('[GeminiService] Backend error:', data.error);
                    clearTimeout(overallTimeout);
                    this.activeAbortController = null;
                    
                    // Check if backend indicates we can continue
                    if (data.error.can_continue) {
                      subject.error({
                        ...data.error,
                        userFriendlyMessage: data.error.message || 'Failed after 3 attempts. Your chat session is still active.'
                      });
                    } else {
                      subject.error(data.error);
                    }
                    return;
                  }
                  
                  // Extract text from Gemini response format
                  if (data.candidates && data.candidates[0]?.content?.parts) {
                    for (const part of data.candidates[0].content.parts) {
                      if (part.text) {
                        // Detect repetitive/stuck pattern (Gemini table bug)
                        const trimmedText = part.text.trim();
                        if (trimmedText === lastChunkText && trimmedText.length > 0) {
                          repetitiveCount++;
                          if (repetitiveCount >= MAX_REPETITIVE) {
                            console.warn(`[GeminiService] Detected ${repetitiveCount} repetitive chunks, aborting stuck stream`);
                            clearTimeout(overallTimeout);
                            this.activeAbortController = null;
                            reader.cancel();
                            subject.error({
                              status: 422,
                              message: 'The model got stuck generating repetitive content. Try asking for the information in a different format (e.g., a list instead of a table).',
                              userFriendlyMessage: 'The model got stuck generating repetitive content. Try asking for the information as a list instead of a table.',
                              can_continue: true
                            });
                            return;
                          }
                        } else {
                          repetitiveCount = 0;
                          lastChunkText = trimmedText;
                        }
                        subject.next(part.text);
                      }
                    }
                  }
                } catch (e) {
                  // Skip invalid JSON lines
                  console.warn('[GeminiService] Failed to parse SSE data:', line, e);
                }
              }
            }

            return readStream();
          });
        };

        return readStream();
      }).catch(err => {
        // Handle fetch errors with retry
        // Don't retry if the request was intentionally aborted
        if (err.name === 'AbortError') {
          console.log('[GeminiService] Stream aborted by user or timeout');
          clearTimeout(overallTimeout);
          this.activeAbortController = null;
          throw { 
            status: 499, 
            message: 'Request was cancelled.',
            userFriendlyMessage: 'Request was cancelled. You can continue your conversation.',
            can_continue: true
          };
        }

        console.error(`[GeminiService] Fetch error on attempt ${attemptNumber}:`, err);
        
        if (attemptNumber < maxAttempts && !abortController.signal.aborted) {
          const delay = Math.min(Math.pow(2, attemptNumber - 1) * 1000, 10000);
          console.log(`[GeminiService] Retrying in ${delay}ms...`);
          return new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              attemptStream(attemptNumber + 1, maxAttempts).then(resolve).catch(reject);
            }, delay);
          });
        }
        
        throw err;
      });
    };

    // Start the streaming with retries
    attemptStream(1, 3).catch(err => {
      clearTimeout(overallTimeout);
      this.activeAbortController = null;
      console.error('[GeminiService] All streaming attempts failed:', err);
      subject.error({
        ...err,
        userFriendlyMessage: err.userFriendlyMessage || 'Failed after 3 attempts. Your chat session is still active - please try again.'
      });
    });

    return subject.asObservable();
  }

  /**
   * Count tokens via backend
   * @param model Model name
   * @param contents Contents to count tokens for
   */
  countTokens(
    model: string,
    contents: any[]
  ): Observable<any> {
    const payload = {
      model,
      contents,
      db_name: this.dbName  // v2 for dev
    };

    return this.http.post<any>(this.geminiCountTokensUrl, payload);
  }
}

